/**
 * <s-bundle-offer> — Multi-buy tier picker inside the product form.
 *
 * Required child markup:
 *   input[data-bundle-quantity]  — Hidden `quantity` input mirrored from the checked tier.
 *   input[data-bundle-tier]      — One radio per tier; value is the tier quantity.
 *   [data-bundle-items]          — Optional per-tier wrapper of item selects (shown only for the checked tier).
 *   select[data-bundle-variant]  — Per-item variant select; only the checked tier's selects stay enabled
 *                                  so the form posts exactly one `bundle_variant` per item.
 *
 * External coupling:
 *   form input[name="id"]        — Kept in sync with the first item select for the native/no-JS fallback.
 */
class SBundleOffer extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches('[data-bundle-tier]')) {
        this.#sync();
      } else if (target instanceof HTMLSelectElement && target.matches('[data-bundle-variant]')) {
        this.#syncVariantId();
      }
    }, { signal: this.#ac.signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const checked = this.querySelector('input[data-bundle-tier]:checked');
    if (!checked) return;

    const quantityInput = this.querySelector('input[data-bundle-quantity]');
    if (quantityInput) quantityInput.value = checked.value;

    for (const tier of this.querySelectorAll('input[data-bundle-tier]')) {
      const label = tier.closest('label');
      const items = label?.querySelector('[data-bundle-items]');
      if (!items) continue;
      const active = tier === checked;
      items.hidden = !active;
      for (const select of items.querySelectorAll('select[data-bundle-variant]')) {
        select.disabled = !active;
      }
    }
    this.#syncVariantId();
  }

  #syncVariantId() {
    const firstSelect = this.querySelector('select[data-bundle-variant]:not(:disabled)');
    const idInput = this.closest('form')?.elements.namedItem('id');
    if (firstSelect && idInput instanceof HTMLInputElement) idInput.value = firstSelect.value;
  }
}

if (!customElements.get('s-bundle-offer')) {
  customElements.define('s-bundle-offer', SBundleOffer);
}
